import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Container, Row, Col, Table, Button } from "react-bootstrap";
import { MdOutlinePerson } from "react-icons/md";
import { AiFillGolden } from "react-icons/ai";
import { RiDeviceRecoverLine } from "react-icons/ri";
import API_DOMAIN from "../config/config";
import dayjs from "dayjs";
import "./tablecus.css";
import "bootstrap/dist/css/bootstrap.min.css";
import LoadingOverlay from "../components/LoadingOverlay";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const DashBoard = () => {
  const [userecoveryData, setUserrecoveryData] = useState([]);
  const [jewelpawnData, setUserjewlpawnData] = useState([]);
  const [customerData, setcustomerData] = useState([]);
  const [interestData, setInterestData] = useState([]);

  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalPawnRecords, setTotalPawnRecords] = useState(0);
  const [totalRecoveries, setTotalRecoveries] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [placeSearchTerm, setPlaceSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const ITEMS_PER_PAGE = 10;

  const [initialLoading, setInitialLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm.trim(), 600);
  const debouncedPlaceTerm = useDebounce(placeSearchTerm.trim(), 600);

  const parsePeriod = (period) => {
    let months = 0;
    let days = 0;
    if (!period) return { months, days };

    const monthMatch = period.match(/(\d+)\s*month(s)?/i);
    const dayMatch = period.match(/(\d+)\s*day(s)?/i);

    if (monthMatch) months = parseInt(monthMatch[1], 10);
    if (dayMatch) days = parseInt(dayMatch[1], 10);

    return { months, days };
  };

  const formatPeriod = (totalMonths) => {
    return `${totalMonths} month${totalMonths !== 1 ? "s" : ""}`;
  };

  const aggregateInterestData = (interestData) => {
    const aggregated = interestData.reduce((acc, item) => {
      const receiptNo = item.receipt_no;
      if (!acc[receiptNo]) {
        acc[receiptNo] = { total_interest_income: 0, total_months: 0 };
      }
      acc[receiptNo].total_interest_income += parseFloat(
        item.interest_income || 0
      );
      const { months } = parsePeriod(item.interest_payment_periods);
      acc[receiptNo].total_months += months;
      return acc;
    }, {});

    Object.keys(aggregated).forEach((receiptNo) => {
      aggregated[receiptNo].interest_payment_periods = formatPeriod(
        aggregated[receiptNo].total_months
      );
    });

    return aggregated;
  };

  const aggregatedInterestData = useMemo(
    () => aggregateInterestData(interestData),
    [interestData]
  );

  const fetchDatajewelpawn = useCallback(
    async (page = 1, isPagination = false) => {
      // Show loading only for initial fetch or pagination
      if (!isPagination) setInitialLoading(true);
      if (isPagination) setPaginationLoading(true);

      try {
        const response = await fetch(`${API_DOMAIN}/pawnjewelry.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "list_light",
            search_text: debouncedSearchTerm,
            place_search: debouncedPlaceTerm,
            page: page,
            limit: ITEMS_PER_PAGE,
          }),
        });

        const data = await response.json();

        if (data.head?.code === 200) {
          setUserjewlpawnData(data.body.pawnjewelry || []);
          setTotalPages(data.body.pagination?.total_pages || 1);
          setTotalRecords(data.body.pagination?.total_records || 0);
          setCurrentPage(data.body.pagination?.current_page || page);

          // Set totals from the API response counts
          if (data.body.counts) {
            setTotalCustomers(data.body.counts.customer || 0);
            setTotalPawnRecords(data.body.counts.pawnjewelry || 0);
            setTotalRecoveries(data.body.counts.recovery || 0);
          }
        } else {
          console.warn("API message:", data.head?.msg);
        }
      } catch (error) {
        console.error("Error fetching pawn jewelry:", error);
      } finally {
        if (!isPagination) setInitialLoading(false);
        if (isPagination) setPaginationLoading(false);
      }
    },
    [debouncedSearchTerm, debouncedPlaceTerm]
  );

  // Initial load only
  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      await fetchDatajewelpawn(1); // no isPagination → initial loading
      setInitialLoading(false);
    };

    loadInitialData();
  }, [fetchDatajewelpawn]);

  // Search / filter change → reset page & fetch (NO LOADER)
  useEffect(() => {
    setCurrentPage(1);
    fetchDatajewelpawn(1); // no isPagination flag → no loader
  }, [debouncedSearchTerm, debouncedPlaceTerm, fetchDatajewelpawn]);

  const handleClear = () => {
    setSearchTerm("");
    setPlaceSearchTerm("");
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      fetchDatajewelpawn(newPage, true); // true → show pagination loader
    }
  };

  return (
    <>
      <LoadingOverlay isLoading={initialLoading} />

      <Container>
        <Row>
          <Col lg="3" md="6" xs="12" className="py-3">
            <div className="counter-card cyan">
              <span>
                <MdOutlinePerson />
              </span>
              <span className="count-numbers plus bold">{totalCustomers}</span>
              <span className="count-name regular">Customer</span>
            </div>
          </Col>
          <Col lg="3" md="6" xs="12" className="py-3">
            <div className="counter-card blue">
              <span>
                <AiFillGolden />
              </span>
              <span className="count-numbers plus bold">
                {totalPawnRecords}
              </span>
              <span className="count-name regular">Jewelry Pawn</span>
            </div>
          </Col>
          <Col lg="3" md="6" xs="12" className="py-3">
            <div className="counter-card green">
              <span>
                <RiDeviceRecoverLine />
              </span>
              <span className="count-numbers plus bold">{totalRecoveries}</span>
              <span className="count-name regular">Jewelry Recovery</span>
            </div>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col lg="12">
            <h5 className="mb-3">Jewelry Pawn Details</h5>

            <Row className="mb-3">
              <Col lg="3" md="6" xs="12" className="mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Mobile number, pawn number or customer name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col lg="3" md="6" xs="12" className="mb-2">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Place"
                  value={placeSearchTerm}
                  onChange={(e) => setPlaceSearchTerm(e.target.value)}
                />
              </Col>
              <Col lg="3" md="6" xs="12">
                <Button variant="secondary" onClick={handleClear}>
                  Clear
                </Button>
              </Col>
            </Row>

            <div className="position-relative">
              {/* Pagination loading overlay */}
              {paginationLoading && (
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75"
                  style={{ zIndex: 5, minHeight: "300px" }}
                >
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              )}

              <div className="balance-table-wrapper">
                <Table bordered hover responsive className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Loan Number</th>
                      <th>Customer Number</th>
                      <th>Customer Name</th>
                      <th>Location</th>
                      <th>Mobile Number</th>
                      <th>Principal Amount</th>
                      <th>Interest Rate</th>
                      <th>Jewelry Weight</th>
                      <th>S/WGT Weight</th>
                      <th>Total Weight</th>
                      <th>Pawned Items</th>
                      <th>Interest Outstanding</th>
                      <th>Interest Paid</th>
                      <th>Total Appraisal</th>
                      <th>Status</th>
                      <th>Interest Overdue Upto 3 Months</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jewelpawnData.length > 0 ? (
                      jewelpawnData.map((item) => (
                        <tr key={item.id || item.receipt_no}>
                          <td>
                            {dayjs(item.pawnjewelry_date).format("DD-MM-YYYY")}
                          </td>
                          <td>{item.receipt_no}</td>
                          <td>{item.customer_no}</td>
                          <td>{item.name}</td>
                          <td>{item.place}</td>
                          <td>{item.mobile_number}</td>
                          <td>{item.original_amount}</td>
                          <td>{item.interest_rate}</td>
                          <td>{item.jewel_weight}</td>
                          <td>{item.net_weight}</td>
                          <td>{item.total_weight}</td>
                          <td>{item.pledged_items}</td>

                          <td>
                            <div className="dashboard">
                              <span>{item.interest_payment_period}</span>
                              <span style={{ color: "green" }}>
                                ₹
                                {parseFloat(
                                  item.interest_payment_amount || 0
                                ).toFixed(2)}
                              </span>
                            </div>
                          </td>

                          <td>
                            <div className="dashboard">
                              <span>
                                {aggregatedInterestData[item.receipt_no]
                                  ?.interest_payment_periods || "N/A"}
                              </span>
                              <span style={{ color: "green" }}>
                                ₹
                                {aggregatedInterestData[
                                  item.receipt_no
                                ]?.total_interest_income?.toFixed(2) || "0"}
                              </span>
                            </div>
                          </td>

                          <td>
                            ₹
                            {(
                              parseFloat(item.original_amount || 0) +
                              parseFloat(item.interest_payment_amount || 0)
                            ).toFixed(2)}
                          </td>

                          <td style={{ textAlign: "center" }}>
                            <span
                              className={`badge ${
                                item.status === "நகை மீட்கபட்டது"
                                  ? "bg-danger"
                                  : "bg-success"
                              }`}
                              style={{
                                minWidth: "20px",
                                minHeight: "20px",
                                display: "inline-block",
                              }}
                            ></span>
                          </td>

                          <td>
                            {(() => {
                              const months =
                                parseInt(item.interest_payment_period) || 0;
                              return months >= 3 ? (
                                <>
                                  <span
                                    className="badge bg-danger"
                                    style={{
                                      minWidth: "20px",
                                      minHeight: "20px",
                                      display: "inline-block",
                                    }}
                                  ></span>
                                  <span style={{ color: "red" }}> Notpaid</span>
                                </>
                              ) : (
                                "-"
                              );
                            })()}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="17" className="text-center">
                          {initialLoading
                            ? "Loading initial data..."
                            : "No records found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </div>

            {/* Pagination */}
            {totalRecords > 0 && (
              <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-3">
                <div>
                  Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} –{" "}
                  {Math.min(currentPage * ITEMS_PER_PAGE, totalRecords)} of{" "}
                  {totalRecords}
                </div>

                <div className="d-flex gap-2">
                  <Button
                    className="bg-primary"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </Button>

                  <span className="align-self-center px-3">
                    Page {currentPage} of {totalPages}
                  </span>

                  <Button
                    className="bg-primary"
                    size="sm"
                    disabled={currentPage >= totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DashBoard;
